import React, { ChangeEvent, FunctionComponent, useRef, useState } from "react";
import { connect, MapDispatchToProps, MapStateToProps } from "react-redux";

import { AlignItems, Icon, IconColor, Layout, SpacingSize } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { bemClassNames, usePopupFlip } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { IAppState, NoneOwnProps } from "@multiweb/types/react";
import { createUpdateWorkflowViewAction } from "../../../actions";
import { LOC_SEARCH_ACTIVITY } from "../../../locKeys";
import { WorkflowEditorProps } from "../../../types";
import { UiWorkflow } from "../../../UiWorkflow";
import { DividerPosition, SearchResultsModal, ToolbarSection } from "../components";
import { getActivitySearchResults } from "../utils";

type ActivitySearchSectionComponentProps = {
    readonly uiWorkflow: UiWorkflow;
    readonly updateWorkflowView: (setId: string, selectedActivityId: string) => void;
    readonly searchActivityText: string;
};

const classNames = bemClassNames("activity-search-section-component");
const searchInputClass = classNames("input");

const ActivitySearchSectionComponent: FunctionComponent<ActivitySearchSectionComponentProps> = ({ uiWorkflow, updateWorkflowView, searchActivityText }) => {
    const [keyword, setKeyword] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const searchResults = getActivitySearchResults(uiWorkflow.workflow, keyword);
    const { topOffset, leftOffset } = usePopupFlip({ triggerRef: inputRef, popupRef: inputRef, opened: searchResults.length > 0 });

    const handleUpdateWorkflowView = (setId: string, selectedActivityId: string) => {
        updateWorkflowView(setId, selectedActivityId);
        emptyInput();
    };

    const handleBlur = () => {
        if (searchResults.length === 0)
            emptyInput();
    };

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setKeyword(e.target.value);
    };

    const emptyInput = () => {
        setKeyword("");
    };

    return (
        <ToolbarSection dividerPosition={DividerPosition.left}>
            <Layout alignItems={AlignItems.center} spacingSizeHorizontal={SpacingSize.xs}>
                <Icon iconCode={IconCode.uiSearch} color={IconColor.grey1} />
                <input ref={inputRef} value={keyword} className={searchInputClass} onChange={onInputChange} onBlur={handleBlur} placeholder={searchActivityText} />
            </Layout>
            {searchResults.length > 0 &&
                <SearchResultsModal
                    results={searchResults}
                    left={leftOffset}
                    top={topOffset}
                    onSearchResultClick={handleUpdateWorkflowView}
                    onOuterSpaceClick={emptyInput} />
            }
        </ToolbarSection>
    );
};

type StateProps = Pick<ActivitySearchSectionComponentProps, "searchActivityText">;

const mapStateToProps: MapStateToProps<StateProps, NoneOwnProps, IAppState> = (state) => ({
    searchActivityText: getPrimaryText(state, LOC_SEARCH_ACTIVITY) ?? "Vyhledat aktivitu...",
});

type DispatchProps = Pick<ActivitySearchSectionComponentProps, "updateWorkflowView">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, WorkflowEditorProps> = (dispatch, { hostKey }) => ({
    updateWorkflowView: (setId, selectedActivityId) => dispatch(createUpdateWorkflowViewAction(hostKey, setId, selectedActivityId, undefined, true)),
});

export const ActivitySearchSection = connect(mapStateToProps, mapDispatchToProps)(ActivitySearchSectionComponent);
